import React, { memo, useState, useMemo } from 'react';
import {
  Select,
  MenuItem,
  Chip,
  Box,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  OutlinedInput,
  SelectChangeEvent,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import './multipleSelect.css';

export interface SelectOption {
  id: string | number;
  label: string;
  value: string;
}

export interface MultipleSearchSelectProps {
  options: SelectOption[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  label?: string;
  maxVisibleChips?: number;
  disabled?: boolean;
  className?: string;
  highlightCheckedColor?: string;
  searchPlaceholder?: string;
}

const MultipleSearchSelect: React.FC<MultipleSearchSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select items...',
  label = 'Select items',
  maxVisibleChips = 2,
  disabled = false,
  className = '',
  highlightCheckedColor = '',
  searchPlaceholder = 'Search...',
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onChange(typeof value === 'string' ? value.split(',') : value);
  };

  const handleDelete = (valueToDelete: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(selectedValues.filter((value) => value !== valueToDelete));
  };

  const getSelectedOptions = () => {
    return options.filter((option) => selectedValues.includes(option.value));
  };

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return options.filter((option) =>
      option.label.toLowerCase().includes(lowerSearchTerm)
    );
  }, [options, searchTerm]);

  const renderValue = (selected: string[]) => {
    const selectedOptions = getSelectedOptions();
    const visibleChips = selectedOptions.slice(0, maxVisibleChips);
    const remainingCount = selectedOptions.length - maxVisibleChips;

    return (
      <Box className="chipContainer">
        {visibleChips.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            className="selectedChip"
            onDelete={disabled ? undefined : handleDelete(option.value)}
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            size="small"
          />
        ))}
        {remainingCount > 0 && (
          <Chip
            label={`+${remainingCount}`}
            className="countChip"
            size="small"
          />
        )}
      </Box>
    );
  };

  const handleOpen = () => {
    setOpen(true);
    setSearchTerm('');
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  return (
    <FormControl className={`multiSelectForm ${className}`} fullWidth>
      <InputLabel className="selectLabel">{label}</InputLabel>
      <Select
        multiple
        value={selectedValues}
        onChange={handleChange}
        input={<OutlinedInput label={label} />}
        renderValue={renderValue}
        className="multiSelectInput"
        disabled={disabled}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        MenuProps={{
          PaperProps: {
            className: 'selectMenuPaper',
            style: {
              maxHeight: 400,
            },
          },
          autoFocus: false,
          disableAutoFocusItem: true,
        }}
        displayEmpty
      >
        {/* Search Field */}
        <Box
          sx={{
            px: 2,
            py: 1,
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => {
              e.stopPropagation();
              setSearchTerm(e.target.value);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {placeholder && selectedValues.length === 0 && !searchTerm && (
          <MenuItem disabled value="" className="placeholderItem">
            <em>{placeholder}</em>
          </MenuItem>
        )}

        {filteredOptions.length === 0 ? (
          <MenuItem disabled className="placeholderItem">
            <em>No results found</em>
          </MenuItem>
        ) : (
          filteredOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              className="selectOption"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <Checkbox
                checked={selectedValues.includes(option.value)}
                className="optionCheckbox"
                size="small"
                sx={{
                  '&.Mui-checked': {
                    color: highlightCheckedColor || undefined,
                    '& svg': { color: highlightCheckedColor || undefined },
                  },
                }}
              />
              <ListItemText primary={option.label} className="optionText" />
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
};

export default memo(MultipleSearchSelect);
